'use client';

import { c as _c } from "react/compiler-runtime";
import type { GroupedCategory } from '@repo/types/maps/map-types';
import { useEffect, useRef, useState } from 'react';
const SWAP_DURATION_MS = 150;
const ENTER_DURATION_MS = 200;
const EASING = 'cubic-bezier(0.165, 0.84, 0.44, 1)';
interface UseCategorySwapAnimationParams {
  allCategories: GroupedCategory[];
  reducedMotion: boolean;
  categoryButtonRefs: React.MutableRefObject<Map<string, HTMLButtonElement>>;
}

/**
 * Manages the display buffer for toolbar category pills so that when a pin
 * selection promotes a new category, the departing chip fades out before
 * the incoming one fades in.
 */
export function useCategorySwapAnimation(t0) {
  const $ = _c(20);
  const {
    allCategories,
    reducedMotion,
    categoryButtonRefs
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const prevCategoryIdsRef = useRef(t1);
  const [displayedCategories, setDisplayedCategories] = useState(allCategories);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const pendingEnterIds = useRef(t2);
  let t3;
  let t4;
  if ($[2] !== allCategories || $[3] !== displayedCategories.length) {
    t3 = () => {
      if (displayedCategories.length === 0 && allCategories.length > 0) {
        setDisplayedCategories(allCategories);
        prevCategoryIdsRef.current = new Set(allCategories.map(_temp));
      }
    };
    t4 = [allCategories, displayedCategories.length];
    $[2] = allCategories;
    $[3] = displayedCategories.length;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== allCategories || $[7] !== categoryButtonRefs || $[8] !== displayedCategories.length || $[9] !== reducedMotion) {
    t5 = () => {
      if (displayedCategories.length === 0) {
        return;
      }
      const currentIds = new Set(allCategories.map(_temp2));
      const prevIds = prevCategoryIdsRef.current;
      const hasChanges = currentIds.size !== prevIds.size || [...currentIds].some(id => !prevIds.has(id));
      if (!hasChanges) {
        setDisplayedCategories(allCategories);
        prevCategoryIdsRef.current = currentIds;
        return;
      }
      const removedIds = [...prevIds].filter(id_0 => !currentIds.has(id_0));
      if (reducedMotion || removedIds.length === 0) {
        setDisplayedCategories(allCategories);
        prevCategoryIdsRef.current = currentIds;
        return;
      }
      for (const id_1 of removedIds) {
        const el = categoryButtonRefs.current.get(id_1);
        if (el) {
          el.style.transition = `opacity ${SWAP_DURATION_MS}ms ${EASING}, transform ${SWAP_DURATION_MS}ms ${EASING}`;
          el.style.opacity = "0";
          el.style.transform = "translateX(8px)";
        }
      }
      const timeout = setTimeout(() => {
        setDisplayedCategories(allCategories);
        prevCategoryIdsRef.current = currentIds;
      }, SWAP_DURATION_MS);
      return () => clearTimeout(timeout);
    };
    t6 = [allCategories, reducedMotion, displayedCategories.length, categoryButtonRefs];
    $[6] = allCategories;
    $[7] = categoryButtonRefs;
    $[8] = displayedCategories.length;
    $[9] = reducedMotion;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[12] !== categoryButtonRefs || $[13] !== displayedCategories || $[14] !== reducedMotion) {
    t7 = () => {
      const currentIds_0 = new Set(displayedCategories.map(_temp3));
      const newIds = [...currentIds_0].filter(id_2 => !prevCategoryIdsRef.current.has(id_2) || pendingEnterIds.current.has(id_2));
      if (newIds.length === 0 || reducedMotion) {
        pendingEnterIds.current.clear();
        return;
      }
      for (const id_3 of newIds) {
        const el_0 = categoryButtonRefs.current.get(id_3);
        if (el_0) {
          el_0.style.transition = "none";
          el_0.style.opacity = "0";
          el_0.style.transform = "translateX(-12px)";
          el_0.getBoundingClientRect();
          el_0.style.transition = `opacity ${ENTER_DURATION_MS}ms ${EASING}, transform ${ENTER_DURATION_MS}ms ${EASING}`;
          el_0.style.opacity = "1";
          el_0.style.transform = "translateX(0)";
        }
      }
      pendingEnterIds.current.clear();
    };
    t8 = [displayedCategories, reducedMotion, categoryButtonRefs];
    $[12] = categoryButtonRefs;
    $[13] = displayedCategories;
    $[14] = reducedMotion;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[17] !== allCategories) {
    t9 = () => {
      const currentIds_1 = new Set(allCategories.map(_temp4));
      for (const id_4 of currentIds_1) {
        if (!prevCategoryIdsRef.current.has(id_4)) {
          pendingEnterIds.current.add(id_4);
        }
      }
    };
    t10 = [allCategories];
    $[17] = allCategories;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
  return displayedCategories;
}
function _temp4(c_2) {
  return c_2.externalId;
}
function _temp3(c_1) {
  return c_1.externalId;
}
function _temp2(c_0) {
  return c_0.externalId;
}
function _temp(c) {
  return c.externalId;
}